import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { reFetchFavoritesTeamAndPlayerAction } from 'redux/modules/favourites';
import { FavoriteItemDto } from 'utils/api/dto';
import * as endpoints from 'utils/api/endpoints';
import { httpMethods, useFetchData } from 'utils/hooks/use-fetch-data';

import { FavoriteToggleHandler } from '../../search-result/search-dropdown/types';

export type UseFavoriteToggleParams = {
    /** the favorite records of the players: a player is removed by the id of its record */
    playerFavItems?: FavoriteItemDto[] | null;
};

type NewFavorite = Parameters<FavoriteToggleHandler>[1];

/**
 * Adds or removes a favorite of the header search. While the request runs the favorites lists are not refetched
 * (reFetchFavoritesTeamAndPlayerAction(false)); after it, also after a failure, they are (true).
 */
export const useFavoriteToggle = ({ playerFavItems }: UseFavoriteToggleParams) => {
    const dispatch = useDispatch();
    const [deletedId, setDeletedId] = useState<number | null>(null);
    const [addedItem, setAddedItem] = useState<NewFavorite | null>(null);

    const finishDelete = useCallback(() => {
        setDeletedId(null);
        dispatch(reFetchFavoritesTeamAndPlayerAction(true));
    }, [dispatch]);

    const finishAdd = useCallback(() => {
        setAddedItem(null);
        dispatch(reFetchFavoritesTeamAndPlayerAction(true));
    }, [dispatch]);

    useFetchData({
        method: httpMethods.delete,
        url: endpoints.favoriteItem(deletedId),
        initialCondition: deletedId !== null,
        responseHandler: finishDelete,
        onRequestError: finishDelete
    });

    useFetchData({
        method: httpMethods.post,
        url: endpoints.favoriteItems,
        initialCondition: !!addedItem,
        payload: { itemEntityId: addedItem?.id, itemType: addedItem?.type },
        responseHandler: finishAdd,
        onRequestError: finishAdd
    });

    const handleFavoriteToggle: FavoriteToggleHandler = useCallback(
        (favoriteId, item) => {
            dispatch(reFetchFavoritesTeamAndPlayerAction(false));
            if (favoriteId) {
                const favItem = playerFavItems?.find(({ itemEntityId }) => itemEntityId === favoriteId);
                setDeletedId(favItem?.id ?? favoriteId);
            } else {
                setAddedItem(item);
            }
        },
        [dispatch, playerFavItems]
    );

    return { handleFavoriteToggle, isUpdating: deletedId !== null || !!addedItem };
};
